<template>

    <div v-if="loading" class="selection:bg-brand-500 selection:text-white overflow-hidden">
        <!-- Loading -->
        <div class="flex items-center justify-center h-[60vh]">
            <div class="flex flex-col items-center justify-center">
                <CoreLoader/>

                <div class="mt-4 text-body/80 font-bold text-sm tracking-widest uppercase">Loading event...</div>
            </div>
        </div>
    </div>
    <template v-else-if="event">
        <div class="selection:bg-brand-500 selection:text-white overflow-hidden">
            <!-- Event -->
            <template v-if="hasSliders">
                <EventSlides :event="event"></EventSlides>
                <div class="h-4"></div>
            </template>
            <template v-else>
                <EventTimer :event="event" :edition="edition" :heading="timerHeading"></EventTimer>
                <!-- Hero -->
                <header v-if="false"                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  class="relative pt-16 pb-0 sm:pt-18">
                    <div
                        class="absolute inset-0 -z-10 bg-[radial-gradient(45%_45%_at_50%_35%,rgba(var(--core-brand-500),0.06)_0%,transparent_100%)]">
                    </div>
                    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <NuxtLink :to="{ name: 'ListOfEvents' }"
                            class="inline-flex items-center gap-2 text-sm font-bold text-brand-500 hover:text-brand-600 transition-colors mb-8 group bg-brand-500/10 px-5 py-2.5 rounded-full shadow-sm">
                            <ArrowLeft class="h-4 w-4 transition-transform group-hover:-translate-x-1" /> Back to Events
                        </NuxtLink>
    
                        <div class="flex flex-wrap items-center gap-3 mb-6">
                            <span :class="[
                                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest',
                                isLive ? 'bg-brand-500 text-white' : 'bg-muted/10 text-body/80 border border-app'
                            ]">
                                <span v-if="isLive" class="h-1.5 w-1.5 rounded-full bg-white animate-pulse"></span>
                                {{ event!.status || 'Scheduled' }}
                            </span>
                            <span class="text-xs text-body/80 font-medium flex items-center gap-1.5">
                                <Calendar class="h-3.5 w-3.5" /> {{ formatDate(event!.startDate) || 'Date to be announced' }}
                            </span>
                        </div>
    
                        <h1 class="text-2xl font-extrabold tracking-tight text-header sm:text-4xl mb-8 leading-[1.1]">
                            {{ event!.title }}
                        </h1>
                    </div>
                </header>
            </template>
        </div>
        <div v-if="!hasSliders && false" class="selection:bg-brand-500 selection:text-white overflow-x-hidden">
            <!-- Featured image -->
            <div class="mx-auto max-w-7xl px-4 mt-4 sm:px-6 lg:px-8">
                <div class="aspect-[21/9] rounded-lg md:rounded-[40px] overflow-hidden border border-app shadow-2xl relative bg-muted">
                    <img v-if="event!.imageUrl" :src="event!.imageUrl ?? undefined" :alt="event!.title"
                        class="w-full h-full object-cover" fetchpriority="high"/>
                    <div v-else
                        class="w-full h-full bg-gradient-to-br from-brand-500/20 via-primary-500/15 to-secondary-500/20 flex items-center justify-center">
                        <Calendar class="h-32 w-32 text-brand-500/30" />
                    </div>
                </div>
            </div>
        </div>
        <div class="sticky top-[72px] z-40 selection:bg-brand-500 selection:text-white overflow-x-hidden" :class="[{'mt-12': !(hasSliders)}]">
            <!-- Content Tabs -->
            <nav class="bg-body/80 backdrop-blur-md border-b border-app shadow-sm transition-all">
                <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div class="flex items-center gap-8 overflow-x-auto no-scrollbar">
                        <button v-for="tab in tabs" :key="tab.id" @click="selectTab(tab.id)" :class="[
                            activeTab === tab.id ? 'border-brand-500 text-brand-700 dark:text-brand-500' : 'border-transparent text-body/80 hover:text-header hover:border-app',
                            'whitespace-nowrap flex items-center gap-2 py-4 border-b-2 text-sm font-bold transition-all'
                        ]">
                            <component :is="tab.icon" class="h-4 w-4" />
                            {{ tab.label }}
                        </button>
                    </div>
                </div>
            </nav>
        </div>
        <div ref="bodyRef" 
            id="event-content-body" 
            class="selection:bg-brand-500 selection:text-white overflow-x-hidden min-h-screen scroll-mt-[120px]"
            :class="activeTab != 'overview' ? sectionClasses(activeTab, 'py-0') : ''">
            <!-- Body -->
            <main class="flex flex-col-reverse md:grid md:grid-cols-1 lg:grid-cols-3 space-x-reverse gap-12">
                <!-- Description -->
                <article :class="showSection('about') ? 'lg:col-span-3' : 'lg:col-span-3'">
                    <EventInfo v-if="showSection('about')" :class="sectionClasses('about')" :event="event" />
                    <EventTickets v-if="showSection('tickets')" :class="sectionClasses('tickets')" :items="event.ticketTypes || []" :event="event" />
                    <EventAgenda v-if="showSection('agenda')" :class="sectionClasses('agenda')" :items="event.agenda || []"
                        :event-location="event.location" :event-title="event.title" />
                    <EventAmenities v-if="showSection('amenities')" :class="sectionClasses('amenities')" :items="event.amenities || []" />
                    <EventSpeakers v-if="showSection('speakers')" :class="sectionClasses('speakers')" :items="event.speakers || []" />
                    <EventExhibitors v-if="showSection('exhibitors')" :class="sectionClasses('exhibitors')" :items="event.exhibitors || []" />
                    <EventSponsors v-if="showSection('sponsors')" :class="sectionClasses('sponsors')" :items="event.sponsors || []" />
                </article>
            </main>
        </div>
    </template>
    <div v-else class="selection:bg-brand-500 selection:text-white pb-24 overflow-x-hidden min-h-screen">
        <!-- Not found -->
        <div class="flex flex-col items-center justify-center h-[60vh] text-center px-4">
            <div class="h-24 w-24 rounded-full bg-brand-500/10 flex items-center justify-center mb-6 text-brand-500 shadow-inner">
                <Calendar class="h-10 w-10" />
            </div>
            <h2 class="text-3xl font-bold text-header mb-4">Event Not Found</h2>
            <p class="text-body/80 mb-8 max-w-md">The event you are looking for doesn't exist or may have been removed.</p>
            <NuxtLink :to="{ name: 'ListOfEvents' }"
                class="btn btn-primary px-8 py-4 rounded-2xl font-bold hover:bg-brand-600 transition-all shadow-xl active:scale-95">
                Return to Events
            </NuxtLink>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import {
    ArrowLeft, Calendar, Clock, MapPin,
    LayoutGrid, Info, Ticket, CalendarClock, Mic, Store, Handshake, Sparkles,
} from 'lucide-vue-next';
import { fetchSingleEvent, fetchUpdates } from '~/utils/api';
import { EventItem } from '~/types';
import { sampleEvents } from '~/components/events/data';
import EventTickets from '~/components/events/EventTickets.vue';
import EventAgenda from '~/components/events/EventAgenda.vue';
import EventSpeakers from '~/components/events/EventSpeakers.vue';
import EventExhibitors from '~/components/events/EventExhibitors.vue';
import EventSponsors from '~/components/events/EventSponsors.vue';
import EventSlides from '~/components/events/EventSlides.vue';
import EventTimer from '~/components/events/EventTimer.vue';
import EventInfo from '~/components/events/EventInfo.vue';
import EventAmenities from '~/components/events/EventAmenities.vue';
import CoreLoader from '@core/components/CoreLoader.vue';

definePageMeta({ name: 'EventSingle', props: true });

const props = defineProps<{ slug?: string }>();

const $route = useRoute();
const slug = computed(() => props.slug || ($route.params.slug as string));

const matches = (e: EventItem) => e.slug === slug.value || e.id === slug.value;

// Fetched during the server render; client-side navigation shows the loader until it arrives.
const { data: event, status } = useAsyncData(`event-${slug.value}`, async (): Promise<EventItem | null> => {
    if (!slug.value) return null;

    try {
        const res = await fetchSingleEvent({ slug: slug.value });
        return sampleEvents.find(matches) || res;
    } catch (e) {
        console.error('Failed to load event', e);
        return sampleEvents.find(matches) || null;
    }
}, { lazy: true });
const loading = computed(() => status.value === 'pending');
const activeTab = ref('overview');

const tabs = computed(() => {
    const list = [
        { id: 'overview', label: 'Overview', icon: LayoutGrid },
        { id: 'about', label: 'About', icon: Info },
    ];
    if (event.value?.ticketTypes?.length) list.push({ id: 'tickets', label: 'Tickets', icon: Ticket });
    if (event.value?.agenda?.length) list.push({ id: 'agenda', label: 'Agenda', icon: CalendarClock });
    if (event.value?.amenities?.length) list.push({ id: 'amenities', label: 'Amenities', icon: Sparkles });
    if (event.value?.speakers?.length) list.push({ id: 'speakers', label: 'Speakers', icon: Mic });
    if (event.value?.exhibitors?.length) list.push({ id: 'exhibitors', label: 'Exhibitors', icon: Store });
    if (event.value?.sponsors?.length) list.push({ id: 'sponsors', label: 'Sponsors', icon: Handshake });

    return list;
}); 

const showSection = (id: string) => activeTab.value === 'overview' || activeTab.value === id;

const bodyRef = ref<HTMLElement | null>(null);

const scrollToBody = () => {
    nextTick(() => {
        bodyRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
};

const selectTab = (id: string) => {
    activeTab.value = id;
    if (history.replaceState) {
        history.replaceState(null, '', `#${id}`);
    } else {
        window.location.hash = id;
    }
    scrollToBody();
};

const sectionClasses = (id: string, defaultClass: string = 'py-16'): string => {
    const pos = sectionPosition(id)%3;

    if(pos === 0) {
        return `bg-brand-200 dark:bg-brand-900 ${defaultClass}`;
    } else if(pos === 1) {
        return `bg-body ${defaultClass}`;
    } else {
        return `bg-brand-100 dark:bg-brand-950 ${defaultClass}`;
    }
}

// position in tabs
const sectionPosition = (id: string) => {
    const index = tabs.value.findIndex(t => t.id === id);
    if (index === -1) return 0;

    return index;
}

const applyHash = () => {
    const id = window.location.hash.replace('#', '');
    if (id && tabs.value.some(t => t.id === id)) {
        activeTab.value = id;
    }
};

const isLive = computed(() =>
    ['upcoming', 'active', 'live', 'open'].includes((event.value?.status || '').toLowerCase())
);

const formatDate = (isoDate?: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('default', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatTime = (isoDate?: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const dateRange = computed(() => {
    const start = formatDate(event.value?.startDate);
    const end = formatDate(event.value?.endDate);
    if (!start) return '';
    if (end && end !== start) return `${start} – ${end}`;
    return start;
});

const hasSliders = computed(() => {
    const list = event.value?.eventSlides;
    // return Array.isArray(list) && list.length > 0;

    return true;
}); 

// Leading number in the title ("25th Designers Meetup" -> 25) for the countdown edition.
const editionMatch = computed(() =>
    (event.value?.title || '').match(/^\s*(\d{1,3})\s*(st|nd|rd|th)?\s+/i)
);
const edition = computed<number | null>(() => editionMatch.value ? Number(editionMatch.value[1]) : null);
const timerHeading = computed(() => {
    const title = event.value?.title || '';
    return editionMatch.value ? title.slice(editionMatch.value[0].length) : title;
});

// Search results and link previews show the event. Unset values (no event) keep app.vue's
// tenant defaults.
const seoTitle = computed(() => event.value?.title || undefined);
const seoDescription = computed(() => {
    // The description may be HTML
    const text = (event.value?.description || event.value?.content || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return text.length > 160 ? `${text.slice(0, 159).trimEnd()}…` : text || undefined;
});
const seoImage = computed(() =>
    event.value?.imageUrl || event.value?.eventSlides?.find(s => s.imageUrl)?.imageUrl || undefined
);

useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogImage: seoImage,
    ogImageAlt: seoTitle,
    twitterTitle: seoTitle,
    twitterDescription: seoDescription,
    twitterImage: seoImage,
});

// The #tab in the URL only exists in the browser: apply it once the page and the event are there.
// (A new slug is a new page: pages are keyed by route.fullPath.)
watch(event, () => nextTick(applyHash));
onMounted(() => {
    applyHash();
    window.addEventListener('hashchange', applyHash);
});
onUnmounted(() => window.removeEventListener('hashchange', applyHash));
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.bg-body {
    background-color: rgb(var(--core-body-bg));
}

.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

.text-body {
    color: rgb(var(--core-body-text));
}

.event-content :deep(p) {
    margin-bottom: 1.25em;
}

.event-content :deep(a) {
    color: rgb(var(--core-brand-500));
    text-decoration: underline;
    text-underline-offset: 4px;
}
</style>
